import { FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useRef } from 'react';

import { authenticatedJsonFetch } from '@perpay-web/authentication/authenticatedJsonFetch';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import FormikTextInput from '@perpay-web/components/base/TextInput/FormikTextInput';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusHeader from '@perpay-web/components/composite/FocusLayout/FocusHeader';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import { HTTPMethod } from '@perpay-web/constants/httpMethods';
import { useDataModule } from '@perpay-web/data-module/useDataModule';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import { BANK_VALIDATION_ENDPOINT } from '@perpay-web/fintech/constants/urls';
import { addBankAccountSchema } from '@perpay-web/fintech/validators/formBased';
import { validateForm } from '@perpay-web/validators/formBased';

import styles from './AddBankAccountForm.scss';

const initialValues = {
    accountNumber: '',
    routingNumber: '',
    bankName: '',
};

const useAddBankAccountForm = (onValidationSuccess) => {
    const formikRef = useRef(null);

    const onSuccess = useCallback(() => {
        onValidationSuccess(formikRef.current.values);
    }, [onValidationSuccess, formikRef]);

    const {
        dataRequest,
        isLoading: isValidating,
        errors: validationErrors,
    } = useDataModule(
        (data) =>
            authenticatedJsonFetch(
                BANK_VALIDATION_ENDPOINT,
                data,
                HTTPMethod.post,
            ),
        {
            onSuccess,
        },
    );

    const formik = useFormik({
        initialValues,
        validateOnMount: true,
        validate: (values) => validateForm(values, addBankAccountSchema),
        onSubmit: (values) => {
            dataRequest({
                accountNumber: values.accountNumber,
                routingNumber: values.routingNumber,
            });
        },
    });

    useEffect(() => {
        formikRef.current = formik;
    }, [formik]);

    const hasFormErrors = Object.keys(formik.errors).length > 0;
    const hasValidationErrors = validationErrors && validationErrors.message;

    return {
        validationErrors,
        hasValidationErrors,
        hasFormErrors,
        formik,
        isValidating,
    };
};

export const AddBankAccountForm = ({
    onValidationSuccess,
    buttonTheme,
    totalSteps,
}) => {
    const {
        validationErrors,
        hasValidationErrors,
        formik,
        isValidating,
        hasFormErrors,
    } = useAddBankAccountForm(onValidationSuccess);

    return (
        <FormikProvider value={formik}>
            <FocusLayout className={styles['focus-layout']}>
                <FocusHeader
                    className={styles['focus-header']}
                    steps={totalSteps}
                    currentStep={1}
                />
                <FocusContent>
                    <Stack className={styles['inner-stack-title']}>
                        <div className={styles['add-bank-title']}>Add Bank</div>
                        <div className={styles['add-bank-subtitle']}>
                            Please enter your bank account details
                        </div>
                    </Stack>
                    <Stack className={styles['inner-stack']}>
                        <FormikTextInput
                            name='accountNumber'
                            label='Checking Account Number'
                        />
                        <FormikTextInput
                            name='routingNumber'
                            label='Routing Number'
                        />
                        <FormikTextInput
                            name='bankName'
                            label='Name (optional)'
                        />

                        {/* Show validation errors from epic */}
                        {hasValidationErrors ? (
                            <div className={styles['validation-error']}>
                                {validationErrors.message}
                            </div>
                        ) : null}
                    </Stack>
                </FocusContent>
                <FocusFooter>
                    {buttonTheme === 'card' ? (
                        <CardCta
                            Component={CustomButton}
                            mode='dark'
                            type='submit'
                            loading={isValidating}
                            onClick={formik.handleSubmit}
                            disabled={
                                isValidating ||
                                (formik.touched && hasFormErrors)
                            }
                        >
                            Add Bank
                        </CardCta>
                    ) : (
                        <CustomButton
                            action='primary'
                            onClick={formik.handleSubmit}
                            className={styles['add-bank-button']}
                            loading={isValidating}
                            disabled={
                                isValidating ||
                                (formik.touched && hasFormErrors)
                            }
                        >
                            Add Bank
                        </CustomButton>
                    )}
                </FocusFooter>
            </FocusLayout>
        </FormikProvider>
    );
};
